import * as React from "react";
import { cn } from "@/lib/class-control";

/**
 * Table Primitives - Componentes de baixo nível para composição de tabelas
 * Baseado no design da imagem com layout slim e cores escuras
 */

// ==========================================
// PRIMITIVES
// ==========================================

// Table Root
interface TablePrimitiveProps extends React.HTMLAttributes<HTMLTableElement> { }

const TablePrimitive = React.forwardRef<HTMLTableElement, TablePrimitiveProps>(
  ({ className, ...props }, ref) => (
    <div className="w-full overflow-auto rounded-lg">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
);
TablePrimitive.displayName = "TablePrimitive";

// Table Header
interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> { }

const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => (
    <thead
      ref={ref}
      className={cn("bg-[#0c1427]", className)}
      {...props}
    />
  )
);
TableHeader.displayName = "TableHeader";

// Table Body
interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> { }

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
);
TableBody.displayName = "TableBody";

// Table Footer
interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> { }

const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn(
        "border-t border-[#0c1427]/60 bg-[#0d1425] font-medium",
        className
      )}
      {...props}
    />
  )
);
TableFooter.displayName = "TableFooter";

// Table Row
interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /**
   * Se true, aplica estilo de hover na linha
   * @default true
   */
  hoverable?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, hoverable = true, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "border-b border-[#0c1427]/60 transition-colors",
        hoverable && "hover:bg-gray-800/30",
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = "TableRow";

// Table Head (Header Cell)
interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /**
   * Alinhamento do texto
   * @default "left"
   */
  align?: "left" | "center" | "right";
}

const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, align = "left", ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "h-[42px] px-5 py-[10px] text-[12px] font-bold text-[#f6f6f6]",
        align === "left" && "text-left",
        align === "center" && "text-center",
        align === "right" && "text-right",
        "[&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

// Table Cell
interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /**
   * Alinhamento do texto
   * @default "left"
   */
  align?: "left" | "center" | "right";
}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, align = "left", ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        "px-5 py-[10px] text-[12px] font-normal text-[#f6f6f6]",
        align === "left" && "text-left",
        align === "center" && "text-center",
        align === "right" && "text-right",
        "[&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = "TableCell";

// Table Caption
interface TableCaptionProps extends React.HTMLAttributes<HTMLTableCaptionElement> { }

const TableCaption = React.forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn("mt-3 text-xs text-gray-400", className)}
      {...props}
    />
  )
);
TableCaption.displayName = "TableCaption";

// ==========================================
// HIGH-LEVEL TABLE COMPONENT
// ==========================================

export interface TableColumn {
  /**
   * Chave única da coluna
   */
  key: string;
  /**
   * Label exibido no header
   */
  label: string;
  /**
   * Alinhamento do conteúdo
   * @default "left"
   */
  align?: "left" | "center" | "right";
  /**
   * Largura da coluna (ex: "120px", "w-32")
   */
  width?: string;
  /**
   * Classes CSS adicionais para o header
   */
  headerClassName?: string;
  /**
   * Classes CSS adicionais para as células
   */
  cellClassName?: string;
}

export type TableRowData = Record<string, React.ReactNode | string | number>;

export interface TableProps {
  /**
   * Configuração das colunas
   */
  columns: TableColumn[];
  /**
   * Dados das linhas
   */
  data: TableRowData[];
  /**
   * Se true, aplica hover nas linhas
   * @default true
   */
  hoverable?: boolean;
  /**
   * Callback ao clicar em uma linha
   */
  onRowClick?: (row: TableRowData, index: number) => void;
  /**
   * Classes adicionais para a tabela
   */
  className?: string;
  /**
   * Classes adicionais para as linhas
   */
  rowClassName?: string | ((row: TableRowData, index: number) => string);
  /**
   * Mensagem quando não há dados
   */
  emptyMessage?: string;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      columns,
      data,
      hoverable = true,
      onRowClick,
      className,
      rowClassName,
      emptyMessage = "Nenhum dado disponível",
    },
    ref
  ) => {
    const handleRowClick = (row: TableRowData, index: number) => {
      if (onRowClick) {
        onRowClick(row, index);
      }
    };

    return (
      <TablePrimitive ref={ref} className={className}>
        <TableHeader>
          <TableRow hoverable={false}>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                align={column.align}
                className={cn(column.width, column.headerClassName)}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {data.length === 0 ? (
            <TableRow hoverable={false}>
              <TableCell
                colSpan={columns.length}
                className="text-center text-gray-400 py-8"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, rowIndex) => {
              const computedRowClassName =
                typeof rowClassName === "function"
                  ? rowClassName(row, rowIndex)
                  : rowClassName;

              return (
                <TableRow
                  key={rowIndex}
                  hoverable={hoverable}
                  onClick={() => handleRowClick(row, rowIndex)}
                  className={cn(
                    onRowClick && "cursor-pointer",
                    computedRowClassName
                  )}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      className={cn(column.width, column.cellClassName)}
                    >
                      {row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </TablePrimitive>
    );
  }
);
Table.displayName = "Table";

// ==========================================
// EXPORTS
// ==========================================

export {
  Table,
  TablePrimitive,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
